import { useContext, useState } from 'react';

import { SubscriptionPlan } from 'app-types/core';
import { FormattedMessage } from 'localization';
import { match } from 'ts-pattern';

import currencyToSymbol from '../../../../utils/currencyToSymbol';
import { SubscriptionContext } from '../../PricingPage/hooks/useSubscriptionContext';
import { PricingItem } from '../PricingItem';
import { useGetPlanFeatureOptions, useGetPlanPricingOptions } from '../hooks/useGetPlanOptions';
import ButtonPricing from '../parts/ButtonPricing';
import NextBilling from '../parts/NextBilling';

type GrowBlockProps = {
  isActive: boolean;
  onSelectPlan: (plan: SubscriptionPlan) => void;
  inChangingPlanProcess: boolean;
  nextBillingDate: string | null | undefined;
  onCancelPlanChanging: () => Promise<void>;
  isCancelingSubscription: boolean;
  isTrial: boolean;
  shopifyBilling: boolean;
  isConquerActive: boolean;
  paymentInPendingProcess: boolean;
};

const GrowBlock = ({
  isActive,
  onSelectPlan,
  inChangingPlanProcess,
  nextBillingDate,
  onCancelPlanChanging,
  isCancelingSubscription,
  isTrial,
  shopifyBilling,
  isConquerActive,
  paymentInPendingProcess,
}: GrowBlockProps) => {
  const [isCancelling, setIsCancelling] = useState(false);
  const contextData = useContext(SubscriptionContext);

  const { growPlanPrice, growPlanDiscontedPrice, currency } = useGetPlanPricingOptions();

  const { growPlanFeatureOptions } = useGetPlanFeatureOptions();

  const growButtonTitle = match<boolean>(true)
    .with(
      isActive && isCancelingSubscription && !isTrial && !shopifyBilling,
      () => 'settings.subs.stay-in-current',
    )
    .with(inChangingPlanProcess && !shopifyBilling, () => 'settings.subs.cancel-downgrade')
    .with(
      (!isActive && !isConquerActive) || isTrial || shopifyBilling,
      () => 'settings.subs.upgrade',
    )
    .otherwise(() => 'settings.subs.downgrade');

  const onSelectHandler = async () => {
    if (
      (inChangingPlanProcess || (isActive && isCancelingSubscription && !isTrial)) &&
      !shopifyBilling
    ) {
      setIsCancelling(true);
      await onCancelPlanChanging();
      setIsCancelling(false);
    } else {
      onSelectPlan(SubscriptionPlan.Grow);
    }
  };

  const showButton = !isActive || inChangingPlanProcess || isCancelingSubscription || isTrial;
  const showNextBillingDate = isActive && !isCancelingSubscription && !isTrial;
  const actualPrice = isActive ? contextData?.subscription?.amount : growPlanPrice;
  const hasDiscountedPrice = growPlanDiscontedPrice !== growPlanPrice;

  return (
    <PricingItem
      isActive={isActive}
      title="Grow"
      className="rounded-[12px] md:rounded-r-none md:rounded-l-[12px]"
      subtitle={
        <>
          <span className={hasDiscountedPrice ? 'line-through mr-1' : ''}>
            <FormattedMessage
              id="settings.subs.per-month"
              values={{ price: currencyToSymbol(currency, actualPrice || 0) }}
            />
          </span>
          {hasDiscountedPrice && (
            <FormattedMessage
              id="settings.subs.per-month"
              values={{
                price: currencyToSymbol(currency, growPlanDiscontedPrice || 0),
              }}
            />
          )}
        </>
      }
      featureList={growPlanFeatureOptions}
    >
      <div className="px-6 w-full">
        {showNextBillingDate && <NextBilling nextBillingDate={nextBillingDate} />}

        {showButton && (
          <ButtonPricing
            disabled={paymentInPendingProcess}
            loading={isCancelling}
            theme="outline"
            onClick={onSelectHandler}
          >
            <FormattedMessage id={growButtonTitle} />
          </ButtonPricing>
        )}
      </div>
    </PricingItem>
  );
};

export default GrowBlock;
